import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = [
    'input',
    'valueInputContainer',
    'fieldset',
    'channels',
    'summary',
  ];

  static values = { flagKeepsChannels: Boolean };

  connect() {
    if (this.activeInput && !this.activeInput.checked) {
      this.fieldsetTarget.disabled = true;
      this.fieldsetTarget.classList.add('d-none');

      if (this.hasValueInputContainerTarget) {
        this.valueInputContainerTarget.classList.add('d-none');
      }
    }

    // Sync channels initial state from the currently-checked urgency radio.
    // Belt-and-braces with the server-rendered initial state (d-none + disabled for Flag).
    this._syncChannels();

    // Sync the summary sub-label to the current live form state on connect.
    this.updateSummary();

    // A disabled <fieldset> submits none of its inputs. When a setting is inactive its
    // fieldset is disabled, so on save the alert-level radios would not be submitted and
    // the server — which REPLACES the settings hash — would null the level. Re-enable the
    // fieldset just before the form submits so the current alert level persists. Runs on
    // the form's own `submit` listener (target phase), which fires before Turbo builds the
    // request body (its document-level bubble listener). The Flag channel fieldset keeps
    // its own `disabled` state, so Flag channels still never submit.
    this._enableFieldsetForSubmit = this._enableFieldsetForSubmit.bind(this);
    this.form = this.element.closest('form');
    if (this.form) {
      this.form.addEventListener('submit', this._enableFieldsetForSubmit);
    }
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener('submit', this._enableFieldsetForSubmit);
    }
  }

  _enableFieldsetForSubmit() {
    if (this.hasFieldsetTarget) {
      this.fieldsetTarget.disabled = false;
    }
  }

  toggleFieldset() {
    if (this.activeInput.checked) {
      this.fieldsetTarget.disabled = false;
      this.fieldsetTarget.classList.remove('d-none');

      if (this.hasValueInputContainerTarget) {
        this.valueInputContainerTarget.classList.remove('d-none');
      }
    } else {
      this.fieldsetTarget.disabled = true;
      this.fieldsetTarget.classList.add('d-none');

      if (this.hasValueInputContainerTarget) {
        this.valueInputContainerTarget.classList.add('d-none');
      }
    }

    this.updateSummary();
  }

  // Called when an urgency radio (Flag/Critical) changes.
  toggleChannels() {
    this._syncChannels();
    this.updateSummary();
  }

  // Rebuilds the accordion sub-label to reflect the current live form state, mirroring
  // `format_property_overview`: empty when the on/off toggle is off; otherwise
  // [value, "Active", severity, active_methods].join(", "). The property VALUE fragment
  // ("37%") comes from a server-only preview component, so JS does not reformat it: on
  // first connect it caches everything before ", Active" as the value prefix and, on each
  // change, re-derives only the dynamic suffix (Active/severity/channels) from the data-*
  // label pieces. (Limitation: an inactive-on-load setting has no server value fragment,
  // so toggling it active shows no value until saved — see the partial's comment.)
  updateSummary() {
    if (!this.hasSummaryTarget) return;

    const el = this.element.dataset;
    const activeLabel = el.alertNotificationsActiveLabel || 'Active';
    const criticalLabel = el.alertNotificationsCriticalLabel || '';
    const flagLabel = el.alertNotificationsFlagLabel || '';
    const wordsConnector = el.alertNotificationsWordsConnector || ', ';
    // Rails `to_sentence` uses a distinct two-element connector (" and "), NOT the
    // three-plus last-word connector (", and ") — keep them separate to match the server.
    const twoWordsConnector = el.alertNotificationsTwoWordsConnector || ' and ';
    const lastWordConnector =
      el.alertNotificationsLastWordConnector || ', and ';

    let channelLabels = {};
    try {
      channelLabels = JSON.parse(el.alertNotificationsChannelLabels || '{}');
    } catch (_) {
      channelLabels = {};
    }

    // Determine if alerts are active (on/off toggle).
    const isActive = this.activeInput ? this.activeInput.checked : false;

    if (!isActive) {
      this.summaryTarget.textContent = '';
      return;
    }

    // Determine severity from the checked urgency radio.
    const checkedRadio = this.element.querySelector(
      'input[type="radio"][name*="_alert_level"]:checked'
    );
    const severityValue = checkedRadio ? checkedRadio.value : null;
    const severityLabel =
      severityValue === 'high'
        ? criticalLabel
        : severityValue === 'low'
        ? flagLabel
        : '';

    // Collect checked channels from the channels fieldset — but only when the fieldset
    // is enabled. A Flag setting's channel fieldset is `disabled` (won't be submitted),
    // so those channels must not appear in the live sub-label. (A `<fieldset disabled>`
    // disables its inputs by inheritance without setting each checkbox's own `.disabled`,
    // so we gate on the fieldset itself.)
    const activeMethods = [];
    if (this.hasChannelsTarget && !this.channelsTarget.disabled) {
      ['email', 'voice', 'sms', 'push'].forEach((channel) => {
        // Channel checkbox names are Rails nested attrs ending in `[<channel>]`
        // (e.g. ...[5][email]) — match the bracketed form, NOT `_<channel>`.
        const checkbox = this.channelsTarget.querySelector(
          `input[type="checkbox"][name*="[${channel}]"]`
        );
        if (checkbox && checkbox.checked) {
          activeMethods.push(channelLabels[channel] || channel);
        }
      });
    }

    // Build active_methods sentence (mirrors Rails to_sentence).
    let activeMethodsStr = '';
    if (activeMethods.length === 1) {
      activeMethodsStr = activeMethods[0];
    } else if (activeMethods.length === 2) {
      activeMethodsStr =
        activeMethods[0] + twoWordsConnector + activeMethods[1];
    } else if (activeMethods.length > 2) {
      const last = activeMethods[activeMethods.length - 1];
      const rest = activeMethods.slice(0, -1).join(wordsConnector);
      activeMethodsStr = rest + lastWordConnector + last;
    }

    // Dynamic suffix = the parts JS owns (everything except the value fragment).
    const parts = [activeLabel, severityLabel, activeMethodsStr].filter(
      Boolean
    );
    const dynamicText = parts.join(wordsConnector);

    // Cache the server-rendered VALUE prefix once (everything before ", Active" on first
    // connect), then reuse it on every rebuild so JS never has to reformat the value.
    let valuePrefix = this.summaryTarget.dataset.summaryValue;
    if (valuePrefix === undefined) {
      const currentText = this.summaryTarget.textContent.trim();
      const activeSep = wordsConnector + activeLabel;
      const activeIdx = currentText.indexOf(activeSep);
      if (activeIdx !== -1) {
        valuePrefix = currentText.slice(0, activeIdx);
      } else {
        // Alert was inactive on initial render (no "Active" in text) — no value prefix to save.
        valuePrefix = '';
      }
      this.summaryTarget.dataset.summaryValue = valuePrefix;
    }

    const fullText = valuePrefix
      ? valuePrefix + wordsConnector + dynamicText
      : dynamicText;
    this.summaryTarget.textContent = fullText;
  }

  get activeInput() {
    return this.inputTargets.find((el) => el.value === 'true');
  }

  // Sync channels visibility from the current urgency radio state.
  // Value keys: "low" = Flag (hide + disable channels); "high" = Critical (show + enable).
  // The server sets `flagKeepsChannels` when a Flag setting may still notify (a check), so
  // Flag keeps the channels shown there.
  // The urgency radio's `name` ends with `_alert_level]` (Rails nested-attr bracket), so
  // match with a *contains* selector — `name$="_alert_level"` would never match.
  // Guards for a missing channels target (non-alerted settings won't have one).
  _syncChannels() {
    if (!this.hasChannelsTarget) return;

    const checkedRadio = this.element.querySelector(
      'input[type="radio"][name*="_alert_level"]:checked'
    );
    if (!checkedRadio) return;

    if (checkedRadio.value === 'low' && !this.flagKeepsChannelsValue) {
      this.channelsTarget.disabled = true;
      this.channelsTarget.classList.add('d-none');
    } else {
      this.channelsTarget.disabled = false;
      this.channelsTarget.classList.remove('d-none');
    }
  }
}
